import { create } from "zustand";
import type { HelpWidgetMessage } from "../types/help_widget_message";

interface RevealingMessageState {
  addMessage: (message: HelpWidgetMessage) => void;
  clearMessages: () => void;
  completeCurrentMessage: () => void;
  isRevealing: boolean;
  queuedMessages: HelpWidgetMessage[];
  setMessages: (messages: HelpWidgetMessage[]) => void;
  visibleMessages: HelpWidgetMessage[];
}

export const useRevealingMessageStore = create<RevealingMessageState>(
  (set, get) => ({
    visibleMessages: [],
    queuedMessages: [],
    isRevealing: false,

    addMessage: (message: HelpWidgetMessage) => {
      const doesExistInQueue = get().queuedMessages.some(
        m => m.id === message.id,
      );
      const doesExistInVisible = get().visibleMessages.some(
        m => m.id === message.id,
      );
      if (doesExistInQueue || doesExistInVisible) return;

      if (get().isRevealing) {
        set(state => ({ queuedMessages: [...state.queuedMessages, message] }));
      } else {
        set(state => ({
          isRevealing: message.show_fading_text_effect,
          visibleMessages: [...state.visibleMessages, message],
        }));
      }
    },

    completeCurrentMessage: () => {
      // Turn off the fading text effect for the last visible message
      const { visibleMessages, queuedMessages } = get();
      const lastMessageIndex = visibleMessages.length - 1;
      const updatedMessages = [...visibleMessages];
      updatedMessages[lastMessageIndex] = {
        ...updatedMessages[lastMessageIndex],
        show_fading_text_effect: false,
      };

      // Process next queued message if any
      if (queuedMessages.length > 0) {
        const [nextMessage, ...remainingMessages] = queuedMessages;

        // Update current message to complete and set isRevealing to false temporarily
        set({
          isRevealing: false,
          visibleMessages: updatedMessages,
        });

        // If next message doesn't need fading effect, add it immediately and process next
        if (nextMessage.show_fading_text_effect) {
          // After delay, add next message and start revealing
          setTimeout(() => {
            set({
              isRevealing: nextMessage.show_fading_text_effect,
              queuedMessages: remainingMessages,
              visibleMessages: [...updatedMessages, nextMessage],
            });
          }, 500);
        } else {
          set({
            isRevealing: false,
            queuedMessages: remainingMessages,
            visibleMessages: [...updatedMessages, nextMessage],
          });
          get().completeCurrentMessage();
        }
      } else {
        set({
          isRevealing: false,
          visibleMessages: updatedMessages,
        });
      }
    },

    setMessages: (messages: HelpWidgetMessage[]) => {
      set({
        isRevealing: false,
        queuedMessages: [],
        visibleMessages: messages,
      });
    },

    clearMessages: () => {
      set({
        isRevealing: false,
        queuedMessages: [],
        visibleMessages: [],
      });
    },
  }),
);
